import type { TQrCodeContent } from '@shared/schemas';

export type CreatorExperienceId =
	| 'website'
	| 'business-card'
	| 'whatsapp'
	| 'social-links'
	| 'menu'
	| 'pdf'
	| 'wifi'
	| 'event'
	| 'coupon'
	| 'vcard'
	| 'app';

export const CREATOR_EXPERIENCES: ReadonlyArray<{
	id: CreatorExperienceId;
	available: boolean;
}> = [
	{ id: 'website', available: true },
	{ id: 'business-card', available: true },
	{ id: 'whatsapp', available: true },
	{ id: 'social-links', available: true },
	{ id: 'menu', available: true },
	{ id: 'pdf', available: true },
	{ id: 'wifi', available: true },
	{ id: 'event', available: true },
	{ id: 'coupon', available: true },
	{ id: 'vcard', available: true },
	{ id: 'app', available: true },
];

export type CreatorContentValues =
	| { experience: 'website'; url: string; isDynamic: boolean }
	| { experience: 'whatsapp'; phone: string; message: string; isDynamic: boolean }
	| { experience: 'wifi'; ssid: string; password?: string; encryption: 'WPA' | 'WEP' | 'nopass' }
	| {
			experience: 'event';
			title: string;
			startDate: string;
			endDate: string;
			location?: string;
			description?: string;
			url?: string;
	  }
	| {
			experience: 'vcard';
			firstName?: string;
			lastName?: string;
			company?: string;
			job?: string;
			phoneMobile?: string;
			phoneBusiness?: string;
			emailPrivate?: string;
			emailBusiness?: string;
			website?: string;
			streetBusiness?: string;
			cityBusiness?: string;
			zipBusiness?: string;
			photo?: string;
			countryBusiness?: string;
			note?: string;
			isDynamic: boolean;
	  };

export function normalizeWebsiteUrl(input: string): string {
	const trimmed = input.trim();
	if (!trimmed || trimmed.length > 1000)
		throw new Error('Enter a website address up to 1,000 characters');
	const candidate = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
	let parsed: URL;

	try {
		parsed = new URL(candidate);
	} catch {
		throw new Error('Website address is invalid');
	}

	if (
		!['http:', 'https:'].includes(parsed.protocol) ||
		!parsed.hostname ||
		parsed.username ||
		parsed.password
	) {
		throw new Error('Enter a valid website address using http or https');
	}

	return parsed.toString();
}

export function buildWhatsAppUrl(phone: string, message: string): string {
	if (message.length > 1000) throw new Error('WhatsApp message must be 1,000 characters or fewer');
	const normalizedPhone = phone.trim().replace(/[\s().-]/g, '');
	if (!/^\+?\d+$/.test(normalizedPhone)) throw new Error('WhatsApp phone number is invalid');
	const digits = normalizedPhone.replace(/^\+/, '');
	if (!/^[1-9]\d{6,14}$/.test(digits)) throw new Error('WhatsApp phone number is invalid');
	const url = `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
	if (url.length > 1000) throw new Error('WhatsApp link is too long; shorten the opening message');
	return url;
}

export function parseCreatorDestination(
	experience: CreatorExperienceId,
	url: string,
): { url: string } | { phone: string; message: string } | null {
	if (experience === 'website') {
		try {
			return { url: normalizeWebsiteUrl(url) };
		} catch {
			return null;
		}
	}

	try {
		const parsed = new URL(url);
		if (
			parsed.protocol !== 'https:' ||
			parsed.hostname !== 'wa.me' ||
			parsed.username ||
			parsed.password
		)
			return null;
		const phone = parsed.pathname.slice(1);
		if (!/^[1-9]\d{6,14}$/.test(phone)) return null;
		const params = [...parsed.searchParams.keys()];
		if (params.some((key) => key !== 'text') || parsed.searchParams.getAll('text').length > 1)
			return null;
		const message = parsed.searchParams.get('text') ?? '';
		if (message.length > 1000 || buildWhatsAppUrl(phone, message) !== url) return null;
		return { phone: `+${phone}`, message };
	} catch {
		return null;
	}
}

export function toQrContent(values: CreatorContentValues): TQrCodeContent {
	if (values.experience === 'website') {
		return {
			type: 'url',
			data: { url: normalizeWebsiteUrl(values.url), isDynamic: values.isDynamic },
		};
	}

	if (values.experience === 'whatsapp') {
		return {
			type: 'url',
			data: { url: buildWhatsAppUrl(values.phone, values.message), isDynamic: values.isDynamic },
		};
	}

	if (values.experience === 'wifi') {
		return {
			type: 'wifi',
			data: {
				ssid: values.ssid.trim(),
				password: values.password ?? '',
				encryption: values.encryption,
			},
		};
	}

	if (values.experience === 'vcard') {
		return {
			type: 'vCard',
			data: {
				...(values.firstName?.trim() ? { firstName: values.firstName.trim() } : {}),
				...(values.lastName?.trim() ? { lastName: values.lastName.trim() } : {}),
				...(values.company?.trim() ? { company: values.company.trim() } : {}),
				...(values.job?.trim() ? { job: values.job.trim() } : {}),
				...(values.phoneMobile?.trim() ? { phoneMobile: values.phoneMobile.trim() } : {}),
				...(values.phoneBusiness?.trim() ? { phoneBusiness: values.phoneBusiness.trim() } : {}),
				...(values.emailPrivate?.trim() ? { emailPrivate: values.emailPrivate.trim() } : {}),
				...(values.emailBusiness?.trim() ? { emailBusiness: values.emailBusiness.trim() } : {}),
				...(values.website?.trim() ? { website: values.website.trim() } : {}),
				...(values.streetBusiness?.trim() ? { streetBusiness: values.streetBusiness.trim() } : {}),
				...(values.cityBusiness?.trim() ? { cityBusiness: values.cityBusiness.trim() } : {}),
				...(values.zipBusiness?.trim() ? { zipBusiness: values.zipBusiness.trim() } : {}),
				...(values.countryBusiness?.trim()
					? { countryBusiness: values.countryBusiness.trim() }
					: {}),
				...(values.photo?.trim() ? { photo: values.photo.trim() } : {}),
				...(values.note?.trim() ? { note: values.note.trim() } : {}),
				isDynamic: values.isDynamic,
			},
		};
	}

	return {
		type: 'event',
		data: {
			title: values.title.trim(),
			startDate: values.startDate,
			endDate: values.endDate,
			...(values.location?.trim() ? { location: values.location.trim() } : {}),
			...(values.description?.trim() ? { description: values.description.trim() } : {}),
			...(values.url?.trim() ? { url: values.url.trim() } : {}),
		},
	};
}
